// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CHARGE_SCHEDULE } from '@/constants/charge-schedule';
import { Currency, SerializableCurrency } from '@/currency/api/types';
import { serializeCurrencyForAPI } from '@/currency/utilities/serialize';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { ProjectLineItem } from '@/project/api/types';
import {
  ChargeRequestItem,
  MilestonePhasesRequest,
  UpdateProjectLineItemParameters,
} from '@/project/api/update-project-line-item';
import {
  BillingPlanFormValues,
  MilestonePhasesInput,
} from '@/project/components/billing-plan-constants';
import { ChargeFormValue } from '@/project/utilities/generate-charges';
import { serializeDateForAPI } from '@/utilities/date-time';
import { Decimal } from 'decimal.js';

interface BuildPayloadInput {
  values: BillingPlanFormValues;
  stored: ProjectLineItem;
  hasDates: boolean;
  chargesModified: boolean;
  localCharges: ChargeFormValue[];
}

/**
 * Builds the sparse-update payload for a billing-plan save. Separated from
 * the drawer's submit handler so every field-inclusion rule is unit-testable
 * — previous regressions silently dropped `milestonePhases` and left stale
 * `chargeFrequency` values on milestone saves because the drawer inlined
 * the rules.
 *
 * Rules:
 * - Flat pricing fields (`pricingMode`, `unitPrice`, `fixedPrice`) are
 *   diffed against the stored line item and included only when they
 *   change.
 * - Schedule fields are gated on `hasDates` — a plan without start/end
 *   dates can't persist a schedule.
 * - In milestone mode, `chargeFrequency` / `invoiceFrequency` /
 *   `invoiceMode` are ALWAYS set to null (the DB CHECK rejects stale
 *   frequency values under MILESTONE), and `milestonePhases` is always
 *   sent (BE requires it present).
 * - In periodic mode, the three frequency fields follow the diff rule;
 *   `milestonePhases` is explicitly set to null only when the plan was
 *   previously on milestone (clearing the stored blob).
 * - Charges are included as a full array whenever they've been modified,
 *   with all date fields normalized to `YYYY-MM-DD` (FormDatePicker
 *   emits ISO timestamps that the BE date parser rejects).
 */
export function buildLineItemUpdatePayload(
  input: BuildPayloadInput
): Partial<UpdateProjectLineItemParameters> {
  const { values, stored, hasDates, chargesModified, localCharges } = input;
  const payload: Partial<UpdateProjectLineItemParameters> = {};

  // Pricing mode and price ride together. The form carries a single
  // `price` currency field; on save we resolve its amount to unit_price or
  // fixed_price based on pricingMode and explicitly null the other column.
  // The line item's single currency (price.isoCode) is attached to whichever
  // column is set, and to total_override below.
  //
  // On a mode change, emit all three so the DB flips both columns and the
  // audit record captures the switch in one event. On an intra-mode price
  // edit, emit only the changed column so the audit isn't padded with
  // no-op null writes on the unchanged side.
  const priceCurrencyCode = values.price.isoCode;
  const formPriceAmount = amountOrNull(values.price.amount);
  const resolvedUnitPrice =
    values.pricingMode === 'PER_UNIT'
      ? toPriceCurrency(formPriceAmount, priceCurrencyCode)
      : null;
  const resolvedFixedPrice =
    values.pricingMode === 'FIXED'
      ? toPriceCurrency(formPriceAmount, priceCurrencyCode)
      : null;
  const modeChanged = values.pricingMode !== (stored.pricingMode ?? null);
  const unitPriceChanged = !isSameWireCurrency(
    resolvedUnitPrice,
    stored.unitPrice
  );
  const fixedPriceChanged = !isSameWireCurrency(
    resolvedFixedPrice,
    stored.fixedPrice
  );
  if (modeChanged) {
    payload.pricingMode = values.pricingMode;
    payload.unitPrice = resolvedUnitPrice;
    payload.fixedPrice = resolvedFixedPrice;
  } else {
    if (unitPriceChanged) payload.unitPrice = resolvedUnitPrice;
    if (fixedPriceChanged) payload.fixedPrice = resolvedFixedPrice;
  }

  // Submit-time alignment for the line item's chargeMode + activation_date.
  // The BE alignment rule is schedule-aware:
  //
  // - MILESTONE: always CONFIRMED + NULL. Each phase activates individually
  //   via MarkChargeBillable; the plan itself has no pending state.
  // - PERIODIC + no start_on: PENDING + NULL until dates are entered.
  // - PERIODIC + dated + form PENDING: PENDING + NULL. The user has flipped
  //   the toggle to draft mode; activation_date is cleared so the BE sees a
  //   genuine pending plan.
  // - PERIODIC + dated + form CONFIRMED: CONFIRMED + activation_date
  //   (form value, falling back to start_on).
  const startOnAPI = serializeDateForAPI(stored.startOn);
  const isMilestoneSchedule =
    values.chargeSchedule === CHARGE_SCHEDULE.MILESTONE;
  let targetPlanChargeMode: 'CONFIRMED' | 'PENDING';
  let targetActivationDate: string | null;
  if (isMilestoneSchedule) {
    targetPlanChargeMode = 'CONFIRMED';
    targetActivationDate = null;
  } else if (startOnAPI === null || values.chargeMode === 'PENDING') {
    targetPlanChargeMode = 'PENDING';
    targetActivationDate = null;
  } else {
    targetPlanChargeMode = 'CONFIRMED';
    if (values.activationDate === null) {
      targetActivationDate = startOnAPI;
    } else {
      targetActivationDate = serializeDateForAPI(values.activationDate);
    }
  }

  if (hasDates) {
    if (values.chargeSchedule !== (stored.chargeSchedule ?? null)) {
      payload.chargeSchedule = values.chargeSchedule;
    }
    const resolvedTotalOverride = toPriceCurrency(
      amountOrNull(values.totalOverride.amount),
      values.totalOverride.isoCode
    );
    if (!isSameWireCurrency(resolvedTotalOverride, stored.totalOverride)) {
      payload.totalOverride = resolvedTotalOverride;
    }

    if (isMilestoneSchedule) {
      payload.chargeFrequency = null;
      payload.invoiceFrequency = null;
      payload.invoiceMode = null;
      // A milestone plan cannot carry a total_override — the BE rejects it.
      // Clear it unconditionally so an override inherited from a prior
      // PERIODIC config can't strand the merged config and fail the save.
      payload.totalOverride = null;
      payload.milestonePhases = toWireMilestonePhases(values.milestonePhases);
    } else {
      if (values.chargeFrequency !== (stored.chargeFrequency ?? null)) {
        payload.chargeFrequency = values.chargeFrequency;
      }
      if (values.invoiceMode !== (stored.invoiceMode ?? null)) {
        payload.invoiceMode = values.invoiceMode;
      }
      if (values.invoiceFrequency !== (stored.invoiceFrequency ?? null)) {
        payload.invoiceFrequency = values.invoiceFrequency;
      }
      if ((stored.milestonePhases ?? null) !== null) {
        payload.milestonePhases = null;
      }
    }

    // Emit chargeMode + activation_date diffs against the derived target.
    // Comparing the target (not the form value) to stored guarantees
    // a payload only fires when the BE state actually differs.
    if (targetPlanChargeMode !== stored.chargeMode) {
      payload.chargeMode = targetPlanChargeMode;
    }
    if (targetActivationDate !== serializeDateForAPI(stored.activationDate)) {
      payload.activationDate = targetActivationDate;
    }
  }

  // Charges are sent only when the user has modified them (regenerate /
  // split / inline edit / add). There is NO plan-mode cascade onto charges
  // through line-item save — activation is regen-driven (PERIODIC) or
  // happens as a backend-only side effect of MarkChargeBillable
  // (MILESTONE). Saving a plan-mode toggle without regenerating leaves
  // existing charges untouched; the BE alignment rule is a per-row CHECK
  // and milestone phases legitimately diverge from their line item.
  if (chargesModified) {
    // The FE array is the source of truth for display order during an
    // edit session — sequence on every charge is rewritten to
    // arrayIndex + 1 so the BE honors the local ordering regardless of
    // what was on `c.sequence` (stale server value on existing rows, 0
    // on client-only generated / split / added rows). The BE's UPDATE
    // path writes the supplied sequence onto existing rows; the CREATE
    // path persists it as the new row's sequence.
    payload.charges = localCharges.map(
      (c, index): ChargeRequestItem => ({
        id: isServerChargeId(c.id) ? c.id : null,
        label: c.label,
        pricingMode: c.pricingMode,
        quantity: c.quantity,
        unitPrice: toNullableChargeCurrency(scrubOutgoingUnitPrice(c)),
        totalPrice: toChargeCurrency(c.totalPrice),
        // Serialize DateTime form state at the API request boundary.
        servicePeriodStart: serializeDateForAPI(c.servicePeriodStart),
        servicePeriodEnd: serializeDateForAPI(c.servicePeriodEnd),
        expectedBillableDate: serializeDateForAPI(c.expectedBillableDate),
        status: c.status,
        isCustomModified: c.isCustomModified,
        triggerType: c.triggerType,
        sequence: index + 1,
      })
    );
  }

  return payload;
}

/** An empty or whitespace-only amount string clears the value (null). */
function amountOrNull(amount: string | null): string | null {
  return amount === null || amount.trim() === '' ? null : amount;
}

/**
 * Builds the request currency from a form amount string and the line item's
 * currency. Returns null when the amount is unset (clears the column).
 */
function toPriceCurrency(
  amount: string | null,
  currencyCode: BillingPlanFormValues['price']['isoCode']
): Currency | null {
  if (amount === null) return null;
  return { amount: new Decimal(amount), isoCode: currencyCode };
}

/** Converts a charge's form currency into the request currency. */
function toChargeCurrency(value: SerializableCurrency): Currency {
  return { amount: new Decimal(value.amount), isoCode: value.isoCode };
}

/** Converts an optional charge form currency into the request currency. */
function toNullableChargeCurrency(
  value: SerializableCurrency | null
): Currency | null {
  return value === null ? null : toChargeCurrency(value);
}

/**
 * Converts the form milestone-phases shape into the request wire shape.
 * Entries carry a bare percentage. The SERIALIZE boundary: form → wire.
 */
function toWireMilestonePhases(
  phases: MilestonePhasesInput | null
): MilestonePhasesRequest | null {
  if (phases === null) return null;
  return {
    mode: 'PERCENTAGE',
    entries: phases.entries.map((entry) => ({
      name: entry.name,
      percentage: entry.percentage,
    })),
  };
}

/**
 * Compares an outgoing currency against the stored Currency by their wire
 * amount and ISO code, so a no-op edit (e.g. "500" vs "500.00") doesn't
 * generate a spurious payload field.
 */
function isSameWireCurrency(
  next: Currency | null,
  stored: Currency | null
): boolean {
  if (next === null) return stored === null;
  if (stored === null) return false;
  return (
    next.isoCode === stored.isoCode &&
    serializeCurrencyForAPI(next).amount ===
      serializeCurrencyForAPI(stored).amount
  );
}

/**
 * Scrubs unit_price to null on outgoing charges that would violate the
 * BE's pricing-mode invariants, so a stale row loaded from the DB cannot
 * block an otherwise-clean save.
 *
 * Rules:
 * - FIXED charges must ship unit_price = null (PER_UNIT concept). Legacy
 *   rows from the old generator stored unit_price = total_price here;
 *   sending that verbatim trips ErrorCodeChargeFixedUnitPriceNotAllowed.
 * - PER_UNIT charges must satisfy unit_price × quantity == total_price
 *   exactly (ErrorCodeChargeTotalPriceMismatch). Legacy rows written
 *   under a total_override may carry a per-unit rate that disagrees with
 *   the stored total; nulling unit_price preserves total_price as the
 *   authoritative amount and lets the save succeed.
 */
function scrubOutgoingUnitPrice(
  c: ChargeFormValue
): SerializableCurrency | null {
  if (c.pricingMode === 'FIXED') return null;
  if (c.unitPrice === null) return null;
  if (c.pricingMode === 'PER_UNIT' && c.quantity > 0) {
    const unit = parseCurrencyAmount(c.unitPrice.amount);
    const total = parseCurrencyAmount(c.totalPrice.amount);
    // An unparseable amount is left for the backend to reject.
    if (
      unit !== null &&
      total !== null &&
      !unit.times(c.quantity).equals(total)
    ) {
      return null;
    }
  }

  return c.unitPrice;
}

/**
 * Client-side IDs use prefixes (gen-, split-, add-). Server-assigned IDs
 * are UUIDs without those prefixes. The update request must drop
 * client-only IDs so the BE assigns fresh ones instead of trying to match
 * a non-existent server row.
 */
function isServerChargeId(id: string | null): boolean {
  return (
    id !== null &&
    !id.startsWith('gen-') &&
    !id.startsWith('split-') &&
    !id.startsWith('add-')
  );
}
